import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/**
 * EV1 met kaart en Bevestig (besluit Frank 2 november 2026): Frank kiest per repo welke map overgaat, ziet op de kaart
 * wat er gebeurt, en start het zelf met Bevestig. Daarna per stap de uitkomst. De map, zijn remote en GitHub blijven
 * zoals ze zijn (backend `services/verhuizing.py`).
 */
export default function RepoVerhuizen({ onKlaar }) {
  const { t } = useTaal();
  const [pad, setPad] = useState("");
  const [naam, setNaam] = useState("");
  const [kaart, setKaart] = useState(null);
  const [uitslag, setUitslag] = useState(null);
  const [bezig, setBezig] = useState(false);

  function fout(error, standaard) {
    const detail = error.response?.data?.detail;
    toast.error(typeof detail === "string" ? t(detail) : standaard);
  }

  async function toon() {
    setBezig(true);
    setUitslag(null);
    try {
      setKaart((await axios.post(`${API}/repos/verhuis/toon`, { pad: pad.trim() })).data);
    } catch (error) {
      fout(error, t("De kaart kon niet worden getoond."));
    } finally {
      setBezig(false);
    }
  }

  async function bevestig() {
    setBezig(true);
    try {
      const { data } = await axios.post(`${API}/repos/verhuis`, { pad: kaart.map, token: kaart.token, name: naam.trim() });
      setUitslag(data);
      toast.success(data.map_ongewijzigd ? t("Verhuisd. De map en zijn remote zijn ongewijzigd.")
        : t("Verhuisd, maar de map veranderde tijdens het verhuizen; kijk de map na."));
      onKlaar?.(data.repo);
    } catch (error) {
      fout(error, t("Verhuizen lukte niet."));
    } finally {
      // De kaart is eenmalig: na Bevestig, gelukt of niet, is er een nieuwe nodig.
      setKaart(null);
      setBezig(false);
    }
  }

  const telling = kaart && { lokaal: kaart.lokale_takken, daar: kaart.alleen_op_github.length, apart: kaart.apart.length,
    na: kaart.takken_na, tags: kaart.tags, n: kaart.vastleggingen };
  return (
    <div className="space-y-2" data-testid="repo-verhuizen">
      <p className="text-xs text-muted-foreground">
        {t("Je kiest zelf welke repo overgaat. De kaart leest de map zonder er iets te schrijven; pas na Bevestig gaat hij over. De map, zijn remote en GitHub blijven zoals ze zijn.")}
      </p>
      <Input value={pad} onChange={(e) => { setPad(e.target.value); setKaart(null); }} placeholder="/Users/naam/project"
             aria-label={t("Volledig pad van de git-map die overgaat")} data-testid="verhuis-pad" />
      <Input value={naam} onChange={(e) => setNaam(e.target.value)} placeholder={t("Naam van de repo (mag leeg)")}
             aria-label={t("Naam van de repo (mag leeg)")} data-testid="verhuis-naam" />
      <Button type="button" variant="outline" className="tap-target" disabled={bezig || !pad.trim()} onClick={toon}
              data-testid="verhuis-toon">
        {bezig && !kaart && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{t("Toon wat er gebeurt")}
      </Button>
      {kaart && (
        <div className="space-y-2 rounded-md border border-amber-500/40 p-2 text-sm" data-testid="verhuis-kaart">
          <p>{t("{lokaal} lokale takken, {daar} alleen op GitHub, {apart} apart onder github/; samen {na} takken, {tags} tags, {n} vastleggingen.",
            telling)}</p>
          <p>{t("Hoofdtak: {tak}. GitHub: {adres}, blijft staan.", { tak: kaart.hoofdtak || "main", adres: kaart.github_adres || "-" })}</p>
          <p className="text-xs text-muted-foreground">{t("Wat er gebeurt, stap voor stap:")}</p>
          <ol className="list-decimal pl-5 text-xs" data-testid="verhuis-stappen">
            {kaart.stappen.map((s) => <li key={s}>{t(s)}</li>)}
          </ol>
          <Button type="button" className="tap-target" disabled={bezig} onClick={bevestig} data-testid="verhuis-bevestig">
            {bezig && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{t("Bevestig: verhuis deze repo")}
          </Button>
        </div>
      )}
      {uitslag && (
        <ol className="list-decimal space-y-0.5 rounded-md border p-2 pl-6 text-xs" data-testid="verhuis-uitslag">
          {uitslag.stappen.map((s) => <li key={s.stap}>{t(s.stap)}: <span className="font-medium">{s.uitkomst}</span></li>)}
        </ol>
      )}
    </div>
  );
}
